import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
    Search, Mail, ArrowRight, Loader2, 
    ShieldCheck, GraduationCap, Clock, AlertCircle
} from 'lucide-react';
import axios from 'axios';

const LecturerStatusCheck = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [institution, setInstitution] = useState(null);

    useEffect(() => {
        const storedInstitution = localStorage.getItem('selectedInstitution');
        if (storedInstitution) {
            setInstitution(JSON.parse(storedInstitution));
        }
    }, []);

    const handleCheckStatus = async (e) => {
        e.preventDefault();
        if (!email) {
            setError('Please enter your email address');
            return;
        }

        setLoading(true);
        setError('');

        try {
            // We just need to check if an application exists
            const response = await axios.get(`/lecturers/application/status/${email}`);
            if (response.data) {
                // If found, navigate to the pending verification page
                navigate(`/lecturer/onboarding/pending?email=${email}`);
            }
        } catch (err) {
            console.error(err);
            if (err.response && err.response.status === 404) {
                setError('No application found with this email address.');
            } else {
                setError('Failed to retrieve application status. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-50 flex flex-col items-center justify-center p-4 md:p-8 font-sans selection:bg-blue-500 selection:text-white">
            <div className="w-full max-w-md relative z-10">
                {/* Branding */}
                <div className="text-center mb-8 flex flex-col items-center">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center shadow-xl mb-4 transform hover:rotate-6 transition-transform">
                        <GraduationCap className="w-10 h-10 text-white" />
                    </div>
                    <h1 className="text-2xl font-black text-gray-900 tracking-tight uppercase">
                        {institution?.name || 'INTUIT LMS'}
                    </h1>
                    <div className="bg-blue-50 px-4 py-1 rounded-full border border-blue-100 mt-2">
                        <span className="text-xs font-black text-blue-600 uppercase tracking-widest">Application Portal</span>
                    </div>
                </div>

                {/* Main Card */}
                <div className="bg-white border border-gray-200 rounded-3xl shadow-xl overflow-hidden p-8 md:p-10">
                    <div className="text-center mb-8">
                        <h2 className="text-2xl font-bold text-gray-900">Check Your Status</h2>
                        <p className="text-gray-500">Enter your application email to continue</p>
                    </div>

                    {error && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-3 animate-in fade-in slide-in-from-top duration-300">
                            <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
                            <p className="text-sm text-red-700 font-medium">{error}</p>
                        </div>
                    )}

                    <form onSubmit={handleCheckStatus} className="space-y-6">
                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                <Mail className="w-4 h-4 text-blue-600" /> Application Email
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="name@example.com"
                                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all shadow-sm"
                                required
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-black py-4 rounded-2xl flex items-center justify-center gap-3 shadow-lg shadow-blue-600/20 transition-all hover:scale-[1.02] active:scale-95"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    RETRIEVING STATUS...
                                </>
                            ) : (
                                <>
                                    VIEW MY APPLICATION <ArrowRight className="w-5 h-5" />
                                </>
                            )}
                        </button>
                    </form>

                    <div className="mt-8 pt-8 border-t border-gray-100 text-center">
                        <p className="text-gray-500 text-sm font-medium mb-4">New here?</p>
                        <Link 
                            to="/lecturer/apply" 
                            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-black text-xs uppercase tracking-widest transition-all"
                        >
                            START NEW APPLICATION <Plus className="w-4 h-4" />
                        </Link>
                    </div>
                </div>

                {/* Footer Info */}
                <div className="mt-10 grid grid-cols-2 gap-4">
                    <div className="bg-white/50 backdrop-blur-sm border border-white/50 rounded-2xl p-4 flex items-center gap-3">
                        <Clock className="w-5 h-5 text-blue-500" />
                        <div>
                            <p className="text-[10px] font-black text-gray-400 uppercase leading-none mb-1">Process</p>
                            <p className="text-xs font-bold text-gray-700">3-5 Day Review</p>
                        </div>
                    </div>
                    <div className="bg-white/50 backdrop-blur-sm border border-white/50 rounded-2xl p-4 flex items-center gap-3">
                        <ShieldCheck className="w-5 h-5 text-emerald-500" />
                        <div>
                            <p className="text-[10px] font-black text-gray-400 uppercase leading-none mb-1">Security</p>
                            <p className="text-xs font-bold text-gray-700">Verified Access</p>
                        </div>
                    </div>
                </div>

                <div className="mt-8 text-center">
                    <Link to="/login?role=lecturer" className="text-gray-400 hover:text-gray-600 text-xs font-black uppercase tracking-widest">
                        Back to Login
                    </Link>
                </div>
            </div>
        </div>
    );
};

const Plus = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
);

export default LecturerStatusCheck;
